// src/services/api.ts

import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { HotelInfo, Metric, AnalysisResponse } from '@types'; // Assuming types are correctly defined
import {
  retryWithBackoff,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cleanMetricValue
} from '@utils';
import { API_CONFIG } from '../config/api';

// Type for API error responses
interface ApiErrorResponse {
  error?: string;
  message?: string;
  details?: unknown;
}

// Type guard for checking if error has response
function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

// Helper to safely get error message
function getErrorMessage(error: unknown): string {
  if (isAxiosErrorWithResponse(error)) {
    return error.response?.data?.error || error.response?.data?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
}

// Get API base URL from centralized config (already includes /api)
const API_HOST = API_CONFIG.BASE_URL;
// No need for API_PREFIX - BASE_URL already includes the /api prefix from config

// Debug information for API configuration
console.log('API Base URL:', API_HOST);
console.log('Environment:', process.env.NODE_ENV);
console.log('Using API at:', API_HOST);

// Determine if we're running locally
const isLocalDevelopment = API_HOST.includes('localhost');

// Token manager singleton for keeping track of authentication state
const tokenManager = {
  getToken: () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },
  setToken: (token: string) => {
    try {
      localStorage.setItem('token', token);
    } catch (error) {
      console.error('Error saving token to localStorage:', error);
    }
  },
  removeToken: () => {
    try {
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Error removing token from localStorage:', error);
    }
  }
};

// Error handling utility
const handleApiError = (error: unknown) => {
  console.error('API Error:', error);

  // Check if this is an axios error with a response
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response;

    // Handle different status codes
    if (status === 401 || status === 403) {
      // Authentication error - clear token and redirect to login
      tokenManager.removeToken();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    // Return standardized error object
    return {
      status,
      message: data?.error || 'Unknown API error',
      details: data?.details || null
    };
  }

  // For non-axios errors
  return {
    status: 500,
    message: error instanceof Error ? error.message : 'Unknown error',
    details: null
  };
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_HOST,  // API_HOST already includes /api from config
  timeout: 300000, // 5 minute timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Always send credentials for all environments
  withCredentials: true,
});

// --- Request Interceptor ---
axiosInstance.interceptors.request.use(
  async (config) => {
    // Check and fix duplicate API prefixes in URL
    if (config.url && config.url.startsWith('/api/') && config.baseURL && config.baseURL.endsWith('/api')) {
      console.log(`Preventing duplicate API prefix in request URL: ${config.url}`);
      // Remove the /api prefix from the URL as it's already in the baseURL
      config.url = config.url.substring(4);
    }

    // Try to get token using the token manager
    const token = tokenManager.getToken();

    // Check if we're requesting an endpoint that doesn't require auth
    const publicEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password'];
    const isPublicEndpoint = publicEndpoints.some(endpoint =>
      config.url?.includes(endpoint)
    );

    // Create a simple flag to prevent auth loops with metrics
    const isMetricsEndpoint = config.url?.includes('/metrics') || false;
    const skipAuth = config.headers?.['X-Skip-Auth'] === 'true';

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Add detailed token debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`API Auth Debug: Token found via tokenManager (length: ${token.length})`);
        try {
          // Simple JWT parts check - doesn't validate signature
          const [header, payload, signature] = token.split('.');
          if (header && payload && signature) {
            const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            console.log(`API Auth Debug: Token payload:`, {
              exp: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : 'missing',
              iat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'missing',
              expiresIn: decoded.exp ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000) + 's' : 'unknown',
              userId: decoded.userId || decoded.sub || 'missing',
            });
          } else {
            console.warn('API Auth Debug: Token doesn\'t appear to be valid JWT format');
          }
        } catch (e) {
          console.warn('API Auth Debug: Error decoding token', e);
        }
      }
    } else if (!isPublicEndpoint) {
      console.warn('API Auth Debug: No token found via tokenManager for protected endpoint');

      // Handle metrics endpoints specially to prevent auth loops
      if (isMetricsEndpoint) {
        if (skipAuth) {
          console.log('Skipping auth for metrics endpoint to prevent loop');
          delete config.headers.Authorization;
        } else {
          // Add a header so we know not to try auth again
          config.headers['X-Skip-Auth'] = 'true';
        }
      }
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        hasAuthHeader: !!config.headers.Authorization,
        baseURL: config.baseURL,
        skipAuth: skipAuth
      });
    }
    return config;
  },
  (error) => {
    console.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  },
);

// --- Response Interceptor ---
axiosInstance.interceptors.response.use(
  (response) => {
    // For successful responses, just return the response
    return response;
  },
  (error) => {
    // Check if this is an auth error for metrics endpoints
    if (axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403) &&
        error.config?.url &&
        error.config.url.includes('/metrics')) {

      console.log('Handling auth error for metrics endpoint:', error.config.url);

      // For metrics endpoints, return empty data instead of error
      return { data: [] };
    }

    // Check for duplicate /api prefix in the URL - this causes 404 errors
    if (axios.isAxiosError(error) &&
        error.response?.status === 404 &&
        error.config?.url &&
        error.config.url.includes('/api/api/')) {

      console.log('Fixing duplicate API prefix in URL:', error.config.url);
      const originalRequest = error.config;
      // Fix the URL by replacing /api/api/ with /api/
      originalRequest.url = originalRequest.url?.replace('/api/api/', '/api/');
      return axiosInstance(originalRequest);
    }

    // Handle 404 errors with retry at different URL format
    // NOTE: Removed retry logic that was adding /api prefix since baseURL already includes it
    // This was causing /api/api/ double prefix issues

    // For other errors, reject the promise
    return Promise.reject(error);
  }
);

// Define API methods
const api = {
  // Auth methods
  login: async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      const { token, user } = response.data;
      tokenManager.setToken(token);
      return { success: true, user };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  },

  logout: () => {
    tokenManager.removeToken();
    return { success: true };
  },

  // Basic HTTP methods
  get: async <T = unknown>(url: string, config?: AxiosRequestConfig) => {
    // Add retry logic for network errors
    const maxRetries = 2;
    let retryCount = 0;

    const executeRequest = async (): Promise<{ data: T }> => {
      try {
        return await axiosInstance.get(url, config);
      } catch (error) {
        console.error(`API GET Error for ${url}:`, error);

        // Check if it's a network error
        if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
          retryCount++;
          if (retryCount <= maxRetries) {
            console.log(`Network error, retrying (${retryCount}/${maxRetries})...`);
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            return executeRequest();
          }

          // For metrics endpoints, return empty data instead of failing
          if (url.includes('/metrics')) {
            console.log('Returning empty data for metrics endpoint after network failure');
            return { data: [] as unknown as T };
          }
        }

        throw error;
      }
    };

    return executeRequest();
  },

  post: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    try {
      return await axiosInstance.post<T>(url, data, config);
    } catch (error) {
      console.error(`API POST Error for ${url}:`, error);
      throw error;
    }
  },

  put: async <T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) => {
    try {
      return await axiosInstance.put<T>(url, data, config);
    } catch (error) {
      console.error(`API PUT Error for ${url}:`, error);
      throw error;
    }
  },

  patch: async <T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) => {
    try {
      return await axiosInstance.patch<T>(url, data, config);
    } catch (error) {
      console.error(`API PATCH Error for ${url}:`, error);
      throw error;
    }
  },

  delete: async (url: string, config?: AxiosRequestConfig) => {
    try {
      return await axiosInstance.delete(url, config);
    } catch (error) {
      console.error(`API DELETE Error for ${url}:`, error);
      throw error;
    }
  },

  // Domain-specific methods
  getHotels: async () => {
    try {
      const response = await axiosInstance.get('/hotels');
      return response.data;
    } catch (error) {
      console.error('Error fetching hotels:', error);
      return [];
    }
  },

  getHotelDetails: async (hotelId: string) => {
    try {
      const response = await axiosInstance.get(`/hotels/${hotelId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching hotel ${hotelId}:`, error);
      throw error;
    }
  },

  getHotelDocuments: async (hotelId: string) => {
    try {
      // Try the agent documents endpoint first (this is where documents are actually stored)
      const response = await axiosInstance.get(`/agents/documents/hotels/${hotelId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for hotel ${hotelId}:`, error);
      // Try alternative endpoint as fallback
      try {
        const fallbackResponse = await axiosInstance.get(`/analysis/hotels/${hotelId}/documents`);
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error(`Fallback endpoint also failed:`, fallbackError);
        return [];
      }
    }
  },

  // Analyze hotel data
  analyze: async (hotelInfo: HotelInfo, metrics: Metric[], hotelAiAnalysis?: unknown): Promise<AnalysisResponse> => {
    try {
      // Check for authentication token
      const token = tokenManager.getToken();
      console.log('⚠️ API analyze function called with token:', !!token, token ? `(length: ${token.length})` : 'missing');

      if (!token) {
        console.error('Authentication token missing');
        const error = new Error('Authentication required - please log in') as Error & { response?: { status: number } };
        error.response = { status: 401 };
        throw error;
      }

      // Extract userId from token to include in request
      let userId: string | null = null;
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          userId = payload.userId || payload.sub;
          console.log('✅ Successfully extracted userId from token:', userId);
        }
      } catch (tokenError) {
        console.error('❌ Error extracting userId from token:', tokenError);
      }

      // Prepare hotel info for analysis (ensure arrays are handled if needed by backend)
      const formattedHotelInfo = {
        ...hotelInfo,
        // Keep amenities as an array if backend expects it directly
        amenities: Array.isArray(hotelInfo.amenities) ? hotelInfo.amenities : [],
        renovation_history: hotelInfo.renovation_history?.map((r) => ({
          cost: parseFloat(r.cost.toString()),
          year: parseInt(r.year.toString()),
          description: r.description?.trim() || '',
        })),
      };
      // Clean and validate metrics before sending
      const formattedMetrics = metrics
        .map((metric) => ({
          ...metric,
          value: cleanMetricValue(metric.value), // Use the imported utility function
          year: parseInt(metric.year.toString()), // Ensure year is a number
        }))
        .filter((m) => !isNaN(m.value) && !isNaN(m.year)); // Filter out invalid metrics

      console.log('API analyze: Creating request with token:', !!token);

      // Try with the user ID included in the payload as a workaround for missing authMiddleware
      return await retryWithBackoff(async () => {
        // First attempt: Send userId directly in the request body as a workaround
        const url = `/analysis/analyze`;
        console.log(`Making POST request to: ${url} with userId in payload as workaround`);

        try {
          // Include userId directly in the payload as a workaround for missing authMiddleware
          const response = await axiosInstance.post(url, {
            userId: userId, // Include userId in payload as workaround for missing authMiddleware
            hotelInfo: formattedHotelInfo,
            metrics: formattedMetrics,
            hotelAiAnalysis: hotelAiAnalysis
          });

          console.log('Analysis response received:', response.status, response.statusText);

          // Validate response structure
          if (!response?.data?.insights) {
            throw new Error('Invalid analysis response format');
          }
          return response.data;
        } catch (err: unknown) {
          if (isAxiosErrorWithResponse(err)) {
            console.error('Analysis request failed:', err.response?.status, err.response?.data);
          } else {
            console.error('Analysis request failed:', err);
          }
          throw err;
        }
      });
    } catch (error: unknown) {
      console.error('❌ Analysis Error:', error);
      if (isAxiosErrorWithResponse(error)) {
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
      }

      // Handle authentication errors
      if (isAxiosErrorWithResponse(error) && error.response?.status === 401) {
        // Clear token if unauthorized
        tokenManager.removeToken();

        // Save current page URL for redirect back after login
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          const currentPath = window.location.pathname;
          localStorage.setItem('loginRedirect', currentPath);
        }

        // Throw specific authentication error
        throw new Error('Authentication required - please log in again');
      }

      // Re-throw a more specific error or the original error
      throw error instanceof Error ? error : new Error('Analysis failed');
    }
  },

  // Update hotel details
  updateHotel: async (hotelId: string, hotelData: Record<string, unknown>) => {
    try {
      // Prepare data for update (ensure arrays are sent correctly)
      const amenities = hotelData.amenities;
      const renovationHistory = hotelData.renovation_history;
      const formattedData = {
        ...hotelData,
        // Send amenities as a JavaScript array
        amenities: Array.isArray(amenities) ? amenities : [],
        // Format renovation history
        renovation_history: Array.isArray(renovationHistory)
          ? renovationHistory.map((r: unknown) => {
              const item = r as Record<string, unknown>;
              return {
                cost: parseFloat(String(item.cost || '0')),
                year: parseInt(String(item.year || new Date().getFullYear())),
                description: typeof item.description === 'string' ? item.description.trim() : '',
              };
            })
          : [],
      };
      // Make the PUT request
      const response = await axiosInstance.put(`/hotels/${hotelId}`, formattedData);
      return response.data;
    } catch (error) {
      // Throw a more specific error
      throw error instanceof Error ? error : new Error('Failed to update hotel');
    }
  },

  // Save new hotel details
  saveHotelDetails: async (hotelData: Record<string, unknown>) => {
    try {
      // Prepare data to send, ensuring 'amenities' is a JavaScript array
      const amenities = hotelData.amenities;
      const dataToSend = {
        ...hotelData,
        // Ensure amenities is always an array before sending
        amenities: Array.isArray(amenities) ? [...amenities] : [],
      };

      console.log('Data being sent to POST /hotels:', dataToSend); // Log data before sending

      // Make the POST request with the corrected data structure
      return await axiosInstance.post('/hotels', dataToSend);
    } catch (error: unknown) {
      // Log the specific error received from the backend if available
      const errorMsg = getErrorMessage(error);
      console.error('Error saving hotel details:', errorMsg);
      // Throw a more informative error message
      throw new Error(errorMsg || 'Failed to save hotel details');
    }
  },

  // Bulk create hotels from spreadsheet import
  bulkCreateHotels: async (hotels: Array<Record<string, unknown>>) => {
    try {
      console.log(`Bulk creating ${hotels.length} hotels...`);
      return await axiosInstance.post('/hotels/bulk', { hotels });
    } catch (error: unknown) {
      const errorMsg = getErrorMessage(error);
      console.error('Error bulk creating hotels:', errorMsg);
      throw new Error(errorMsg || 'Failed to bulk create hotels');
    }
  },

  // Create a new deal
  createDeal: async (dealData: Record<string, unknown>) => {
    try {
      const hotelInfo = dealData.hotelInfo as Record<string, unknown> | undefined;
      const metrics = dealData.metrics as Array<Record<string, unknown>> | undefined;

      // Prepare payload for creating a deal
      const formattedPayload: Record<string, unknown> = {
        ...dealData,
        // Structure hotel data for the deal
        hotel_data: hotelInfo ? {
          name: hotelInfo.name,
          address: hotelInfo.address,
          city: hotelInfo.city,
          state: hotelInfo.state,
        } : {},
        // Format financial metrics
        financial_metrics: Array.isArray(metrics) ? metrics.map((m) => ({
          category: typeof m.category === 'string' ? m.category.trim().toLowerCase() : '',
          value: Number(m.value), // Ensure value is a number
          year: m.year || new Date().getFullYear(), // Default year if missing
        })) : [],
      };
      // Remove original metrics array if it exists (backend might expect only financial_metrics)
      delete formattedPayload.metrics;

      // Make the POST request
      const response = await axiosInstance.post('/deals', formattedPayload);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Create Deal Error:', error);
      // Throw a more informative error
      throw new Error(getErrorMessage(error) || 'Failed to create deal');
    }
  },

  // Get all deals
  getDeals: async () => {
    try {
      // Make the GET request
      const response = await axiosInstance.get('/deals');
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Get Deals Error:', error);
      // Throw a more informative error
      throw new Error(getErrorMessage(error) || 'Failed to fetch deals');
    }
  },

  // Delete a specific deal
  deleteDeal: async (dealId: string) => {
    try {
      console.log(`🗑️ Attempting to delete deal ${dealId}...`);
      // Retrieve authentication token
      const token = localStorage.getItem('token');
      console.log(`Token available for deletion: ${!!token}`);
      // Construct the full URL for the delete request
      const url = `${API_HOST}/deals/${dealId}`;
      console.log(`Making DELETE request to: ${url}`);
      // Make the DELETE request using axios directly for clarity
      const response = await axios({
        method: 'DELETE',
        url: url, // Use the constructed URL
        headers: {
          'Content-Type': 'application/json',
          // Include Authorization header if token exists
          Authorization: token ? `Bearer ${token}` : '',
        },
        withCredentials: !isLocalDevelopment, // Include credentials based on environment
      });
      console.log('✅ Delete deal successful:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Delete Deal Error:', error);
      // Log detailed error information if available
      if (isAxiosErrorWithResponse(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response headers:', error.response?.headers);
        console.error('Response data:', error.response?.data);
      }
      // Throw a more specific error message
      throw new Error(getErrorMessage(error) || 'Failed to delete deal');
    }
  },

  // Get market research data
  getMarketResearch: async (hotelId: string) => {
    try {
      // Make the GET request
      const response = await axiosInstance.get(`/market-research/${hotelId}/market-analysis`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch market research:', error);
      throw error; // Re-throw the original error
    }
  },

  // Generate market research data
  generateMarketResearch: async (hotelId: string, params = {}) => {
    try {
      // Make the POST request with parameters
      const response = await axiosInstance.post(`/market-research/${hotelId}/market-analysis`, {
        marketParams: params,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate market research:', error);
      throw error; // Re-throw the original error
    }
  },

  // Save a specific hotel metric (e.g., occupancy, ADR)
  saveHotelMetric: async (hotelId: string, type: string, year: number, value: number) => {
    try {
      // Make the POST request
      const response = await axiosInstance.post('/hotels/metrics', {
        hotelId,
        type, // The category of the metric (e.g., 'occupancy')
        year,
        value,
      });
      return response.data;
    } catch (error: unknown) {
      // Throw a more specific error message
      throw new Error(getErrorMessage(error) || `Failed to save ${type} metric data`);
    }
  },

  // Get all metrics for a specific hotel
  getHotelMetrics: async (hotelId: string) => {
    try {
      // Ensure URL doesn't have duplicate /api prefix - this is a defensive measure
      const endpoint = `/hotels/${hotelId}/metrics`;

      // Make the GET request
      console.log(`Fetching metrics for hotel ${hotelId} with endpoint: ${endpoint}`);
      const response = await axiosInstance.get(endpoint);
      return response.data;
    } catch (error: unknown) {
      // Throw a more specific error message
      throw new Error(getErrorMessage(error) || 'Failed to fetch hotel metrics');
    }
  },

  // Upload documents associated with a hotel
  uploadHotelDocuments: async (hotelId: string, files: File[], category?: string, description?: string) => {
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('hotelId', hotelId);
      // Append each file to the FormData
      files.forEach((file) => formData.append('files', file));
      // Append optional category and description
      if (category) formData.append('category', category);
      if (description) formData.append('description', description);

      // Make the POST request with multipart/form-data header
      const response = await axiosInstance.post('/agents/documents/hotels/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: unknown) {
      // Throw a more specific error message
      throw new Error(getErrorMessage(error) || 'Failed to upload hotel documents');
    }
  },

  // Get URL for downloading a specific hotel document
  downloadHotelDocument: (documentId: string) => {
    // Construct the download URL
    return `${API_HOST}/agents/documents/hotels/documents/${documentId}/download`;
  },

  // Delete a specific hotel document
  deleteHotelDocument: async (documentId: string) => {
    try {
      // Make the DELETE request
      const response = await axiosInstance.delete(`/agents/documents/hotels/documents/${documentId}`);
      return response.data;
    } catch (error: unknown) {
      // Throw a more specific error message
      throw new Error(getErrorMessage(error) || 'Failed to delete hotel document');
    }
  },

  // Get all financial data for a specific hotel, trying multiple endpoints
  getHotelFinancialData: async (hotelId: string) => {
    try {
      console.log(`Fetching financial data for hotel ${hotelId}`);

      // First try the new comprehensive endpoint
      try {
        const comprehensiveData = await axiosInstance.get(`/hotels/${hotelId}/comprehensive-financials`);
        if (comprehensiveData.data && comprehensiveData.data.status === 'success') {
          console.log(`Found comprehensive financial data: ${comprehensiveData.data.summary.totalFinancialRecords} records`);
          return {
            source: 'comprehensive',
            data: comprehensiveData.data
          };
        }
      } catch {
        console.warn('Comprehensive financials endpoint failed, trying individual endpoints');
      }

      // First try confirmed financials endpoint
      try {
        const confirmedData = await axiosInstance.get(`/hotels/${hotelId}/confirmed-financials`);
        if (confirmedData.data && confirmedData.data.length > 0) {
          console.log(`Found ${confirmedData.data.length} confirmed financial records`);
          return {
            source: 'confirmed-financials',
            data: confirmedData.data
          };
        }
      } catch {
        console.warn('Confirmed financials endpoint failed, trying OCR data');
      }

      // Try OCR data if no confirmed financials
      try {
        const ocrData = await axiosInstance.get(`/agents/documents/hotel/${hotelId}/financials`);
        if (ocrData.data && ocrData.data.length > 0) {
          console.log(`Found ${ocrData.data.length} OCR financial records`);
          return {
            source: 'ocr',
            data: ocrData.data
          };
        }
      } catch {
        console.warn('OCR financials endpoint failed');
      }

      // Legacy attempt - try metrics endpoint as last resort
      try {
        const metricsData = await axiosInstance.get(`/hotels/${hotelId}/metrics`);
        if (metricsData.data && metricsData.data.length > 0) {
          console.log(`Found ${metricsData.data.length} metrics records`);
          return {
            source: 'metrics',
            data: metricsData.data
          };
        }
      } catch {
        console.warn('Metrics endpoint failed');
      }

      // If we reach here, no data was found
      console.log('No financial data found for hotel');
      return {
        source: null,
        data: []
      };
    } catch (error: unknown) {
      console.error('Error fetching hotel financial data:', error);
      throw new Error(getErrorMessage(error) || 'Failed to fetch hotel financial data');
    }
  },

  // Get comprehensive financial data for a hotel (new method)
  getComprehensiveHotelData: async (hotelId: string) => {
    try {
      console.log(`Fetching comprehensive data for hotel ${hotelId}`);
      const response = await axiosInstance.get(`/hotels/${hotelId}/comprehensive-financials`);

      if (response.data && response.data.status === 'success') {
        console.log(`Comprehensive data retrieved: ${response.data.summary.totalFinancialRecords} financial records, ${response.data.summary.totalDocuments} documents`);
        return response.data;
      } else {
        throw new Error('Invalid response format from comprehensive financials endpoint');
      }
    } catch (error: unknown) {
      console.error('Error fetching comprehensive hotel data:', error);
      throw new Error(getErrorMessage(error) || 'Failed to fetch comprehensive hotel data');
    }
  },

  // Sync financial data to ensure valuation analysis sees latest changes
  syncFinancialData: async (hotelId: string) => {
    try {
      console.log(`Syncing financial data for hotel ${hotelId}`);
      const response = await axiosInstance.post(`/hotels/${hotelId}/sync-financials`);

      if (response.data) {
        console.log(`✅ Financial sync completed: ${response.data.syncedRecords} records synchronized`);
        return response.data;
      } else {
        throw new Error('Invalid response format from sync endpoint');
      }
    } catch (error: unknown) {
      console.error('Error syncing financial data:', error);
      throw new Error(getErrorMessage(error) || 'Failed to sync financial data');
    }
  },

  // Investment & Financing endpoints
  getInvestmentData: async (hotelId: string, scenarioId?: string) => {
    try {
      const url = scenarioId
        ? `/investment/${hotelId}/comprehensive?scenarioId=${scenarioId}`
        : `/investment/${hotelId}/comprehensive`;
      console.log('🌐 api.getInvestmentData: Making request to:', url);
      const response = await axiosInstance.get(url);
      console.log('🌐 api.getInvestmentData: Response received:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        fullResponse: response.data
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching investment data:', error);
      throw new Error(getErrorMessage(error) || 'Failed to fetch investment data');
    }
  },

  createFinancing: async (hotelId: string, financing: any) => {
    try {
      const response = await axiosInstance.post(`/investment/${hotelId}/financing`, financing);
      return response.data;
    } catch (error: any) {
      console.error('Error creating financing:', error);
      // Preserve the original error object with response data
      if (error.response) {
        const apiError: any = new Error(
          error.response.data?.message ||
          error.response.data?.error ||
          getErrorMessage(error) ||
          'Failed to create financing'
        );
        apiError.response = error.response;
        throw apiError;
      }
      throw new Error(getErrorMessage(error) || 'Failed to create financing');
    }
  },

  calculateReturns: async (hotelId: string, assumptions: any) => {
    try {
      const response = await axiosInstance.post(`/investment/${hotelId}/returns`, assumptions);
      return response.data;
    } catch (error: unknown) {
      console.error('Error calculating returns:', error);
      throw new Error(getErrorMessage(error) || 'Failed to calculate returns');
    }
  },

  generateProForma: async (hotelId: string, assumptions: any) => {
    try {
      const response = await axiosInstance.post(`/investment/${hotelId}/pro-forma`, assumptions);
      return response.data;
    } catch (error: unknown) {
      console.error('Error generating pro forma:', error);
      throw new Error(getErrorMessage(error) || 'Failed to generate pro forma');
    }
  },
};

// ========================================
// Hotel Rate Benchmarking
// ========================================
export const hotelRateService = {
  searchRates: async (params: {
    query: string;
    checkInDate: string;
    checkOutDate: string;
    adults?: number;
    currency?: string;
    forceRefresh?: boolean;
  }) => {
    const response = await axiosInstance.post('/hotel-rates/search', params);
    return response.data;
  },

  getBenchmark: async (hotelId: string, params: {
    checkInDate?: string;
    checkOutDate?: string;
    forceRefresh?: boolean;
  } = {}) => {
    const response = await axiosInstance.post(`/hotel-rates/${hotelId}/benchmark`, params);
    return response.data;
  },

  getCachedRates: async (hotelId: string, checkInDate?: string, checkOutDate?: string) => {
    const params = new URLSearchParams();
    if (checkInDate) params.append('checkInDate', checkInDate);
    if (checkOutDate) params.append('checkOutDate', checkOutDate);
    const response = await axiosInstance.get(`/hotel-rates/${hotelId}/cached?${params.toString()}`);
    return response.data;
  },

  getProviderInfo: async () => {
    const response = await axiosInstance.get('/hotel-rates/provider');
    return response.data;
  },

  clearCache: async () => {
    const response = await axiosInstance.delete('/hotel-rates/cache');
    return response.data;
  },
};

// Export the API, token manager, and necessary utility functions
export default api;
export { axiosInstance }; // Export the original axios instance if needed
export const auth = tokenManager;